import { useQuery } from '@apollo/client';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import {
  Loader,
  Headline,
  ListGridSearchBar,
  type LabeledValue,
} from '@volumental/common';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandsQuery } from '../../../api/queries/survey-query';
import { useScannerStore } from '../../../store';
import { type BrandStep } from '../../../store/survey';
import { findWithFallback } from './find-value';
import { selectTimeoutMs } from './constants';

interface BrandSelectorProps {
  survey: BrandStep;
  onClose: (brand: LabeledValue<string>) => void;
}

export function BrandSelector({
  survey: { brand },
  onClose,
}: BrandSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const { flowType } = useScannerStore((store) => store);
  const { data, loading } = useQuery(BrandsQuery);
  const [optimisticValue, setOptimisticValue] = useState(brand?.value);

  const onReady = (selected: string): void => {
    setOptimisticValue(selected);
    if (brand?.value !== selected) {
      usageTracker.track(UsageTrackerEvent.AddBrandToSurvey, {
        brand: selected,
        sizing_flow_type: flowType,
      });
    }

    const selectedBrand = findWithFallback(selected, brands);

    setTimeout(() => {
      onClose(selectedBrand);
    }, selectTimeoutMs);
  };

  if (loading || !data?.recommendableBrands) {
    return <Loader />;
  }
  const brands = data.recommendableBrands.map((item) => ({
    value: item.value,
    label: item.explanation ?? item.value,
  }));
  return (
    <>
      <Headline style={{ fontSize: '21px' }}>{t('shoeBrand')}</Headline>
      <ListGridSearchBar
        options={brands}
        onSelect={onReady}
        selected={optimisticValue}
        groupAriaLabel={t('ariaLabels.brands')}
        placeHolder={t('shoeBrandPlaceHolder')}
        noMatchDescription={t('tryOtherWord')}
        noMatchTitle={t('noMatch')}
        checkMarkAltText={t('imageAlt.checkMark')}
        ariaSearchLabel={t('ariaLabels.searchInput')}
        crossAriaLabel={t('ariaLabels.clearBT')}
        searchIconAlt={t('imageAlt.searchIcon')}
      />
    </>
  );
}
